/**
 * The bookmarks bar under a conversation's header (Slack; core ChannelBookmarksViewModel): chips for links (the
 * site's favicon), files, pages and folders (a folder opens its bookmarks in a popover); drag chips to reorder them,
 * in the bar or inside a folder, or onto a folder's middle to move them into it; right-click / menu: Edit or Rename,
 * Move to a folder / out of it, Remove. "+" adds a link, a file shared in the conversation, a page, or a folder.
 */
import type { ChannelBookmark } from "@bundle/contracts";
import { Bookmarks, ChannelBookmarksViewModel, Files, Shell } from "@bundle/core";
import type { IconName } from "@bundle/tokens";
import { useCallback, useState } from "react";
import {
  Button,
  Icon,
  IconButton,
  Image,
  ListRow,
  Menu,
  Popover,
  Pressable,
  Sheet,
  SortableList,
  Spinner,
  Stack,
  Text,
  TextField,
  openLink,
  type MenuEntry,
} from "../primitives/index.ts";
import { useT, useTr } from "../i18n/I18nProvider.tsx";
import { useOwned } from "../hooks/owned.ts";
import { useViewModel, useWorkspace } from "../hooks/index.ts";
import { useNavigation } from "../context/AppContext.tsx";

type T = ReturnType<typeof useT>;
type BookmarkNode = Bookmarks.BookmarkNode;

function kindIcon(b: ChannelBookmark): IconName {
  switch (Bookmarks.bookmarkKind(b)) {
    case "folder":
      return "folder";
    case "page":
      return "page";
    case "file":
      return Files.fileIcon(Files.fileKind(b.mime ?? "", b.title)) as IconName;
    default:
      return "link";
  }
}

function BookmarkIcon({ b }: { b: ChannelBookmark }) {
  const icon = <Icon name={kindIcon(b)} size="icon.sm" color="text.secondary" />;
  const favicon = Bookmarks.bookmarkKind(b) === "link" ? Bookmarks.bookmarkFaviconUrl(b.url) : undefined;
  return favicon === undefined ? icon : <Image src={favicon} alt="" width={16} height={16} radius="xs" fallback={icon} />;
}

/** Edit / Rename, Move to <folder> / Move out of folder, Remove / Delete folder. */
function bookmarkMenu(vm: ChannelBookmarksViewModel, tree: BookmarkNode[], b: ChannelBookmark, t: T): MenuEntry[] {
  const kind = Bookmarks.bookmarkKind(b);
  const entries: MenuEntry[] = [{ id: "edit", label: t(kind === "link" ? "bookmarks.edit" : "bookmarks.editFolder"), icon: "edit", onSelect: () => vm.openEdit(b.id) }];
  if (kind !== "folder") {
    for (const n of tree) {
      if (Bookmarks.bookmarkKind(n.bookmark) !== "folder" || n.bookmark.id === b.parentId) continue;
      entries.push({ id: `move-${n.bookmark.id}`, label: t("bookmarks.moveTo", { folder: n.bookmark.title }), icon: "folder", onSelect: () => vm.moveToFolder(b.id, n.bookmark.id) });
    }
    if (b.parentId !== undefined && tree.some((n) => n.bookmark.id === b.parentId)) entries.push({ id: "move-out", label: t("bookmarks.moveOut"), icon: "arrowUp", onSelect: () => vm.moveToFolder(b.id, undefined) });
  }
  entries.push({ id: "sep", separator: true });
  entries.push({ id: "remove", label: t(kind === "folder" ? "bookmarks.removeFolder" : "bookmarks.remove"), icon: "trash", destructive: true, onSelect: () => vm.remove(b.id) });
  return entries;
}

export function ChannelBookmarksBar({ channelId, testID }: { channelId: string; testID: string }) {
  const t = useT();
  const tr = useTr();
  const ws = useWorkspace();
  const nav = useNavigation();
  const create = useCallback(() => new ChannelBookmarksViewModel(ws.vm, channelId), [ws.vm, channelId]);
  const vm = useOwned(`bookmarks:${ws.vm.wsId}:${channelId}`, create);
  const s = useViewModel(vm);
  const [openFolder, setOpenFolder] = useState<string | undefined>(undefined);
  const e = s.editor;
  const p = s.picker;
  if (s.tree.length === 0 && !s.canEdit) return null;

  const open = (b: ChannelBookmark) => {
    const kind = Bookmarks.bookmarkKind(b);
    if (kind === "link") openLink(b.url);
    else if (kind === "file" && b.fileId !== undefined) nav.open(Shell.formatUrl({ type: "file", ws: ws.slug, fileId: b.fileId }), nav.canSplit ? "split" : "current");
    else if (kind === "page" && b.pageId !== undefined) nav.open(Shell.formatUrl({ type: "page", ws: ws.slug, pageId: b.pageId }), "current");
  };

  const chip = (b: ChannelBookmark, onPress: () => void, trailing?: IconName) => {
    const menu = s.canEdit ? bookmarkMenu(vm, s.tree, b, t) : undefined;
    return (
      <Pressable onPress={onPress} direction="horizontal" radius="sm" accessibilityLabel={b.title} tooltip={Bookmarks.bookmarkKind(b) === "link" ? b.url : undefined} contextMenu={menu} testID={`${testID}-${b.id}-open`}>
        <Stack direction="horizontal" align="center" gap="1" paddingX="2" paddingY="1">
          <BookmarkIcon b={b} />
          <Text variant="footnote" lines={1}>
            {b.title}
          </Text>
          {trailing === undefined ? null : <Icon name={trailing} size="icon.sm" color="text.secondary" />}
        </Stack>
      </Pressable>
    );
  };

  const folderPopover = (n: BookmarkNode) => (
    <Popover
      open={openFolder === n.bookmark.id}
      onClose={() => setOpenFolder(undefined)}
      side="bottom"
      align="start"
      width="picker"
      anchor={chip(n.bookmark, () => setOpenFolder(openFolder === n.bookmark.id ? undefined : n.bookmark.id), "chevronDown")}
    >
      <Stack gap="1" padding="2" testID={`${testID}-${n.bookmark.id}-folder`}>
        <Text variant="footnote" weight="semibold" color="text.secondary">
          {n.bookmark.title}
        </Text>
        {n.children.length === 0 ? (
          <Text variant="footnote" color="text.secondary">
            {t("bookmarks.emptyFolder")}
          </Text>
        ) : (
          <SortableList
            items={n.children}
            direction="vertical"
            gap="0"
            disabled={!s.canEdit}
            onMove={(id, index) => vm.move(id, n.bookmark.id, index)}
            testID={`${testID}-${n.bookmark.id}-items`}
            renderItem={(b) => (
              chip(b, () => {
                setOpenFolder(undefined);
                open(b);
              })
            )}
          />
        )}
        {s.canEdit ? (
          <Button
            variant="ghost"
            size="sm"
            icon="plus"
            label={t("bookmarks.addToFolder")}
            onPress={() => {
              setOpenFolder(undefined);
              vm.openAdd(n.bookmark.id);
            }}
            testID={`${testID}-${n.bookmark.id}-add`}
          />
        ) : null}
      </Stack>
    </Popover>
  );

  const addMenu: MenuEntry[] = [
    { id: "link", label: t("bookmarks.addLink"), icon: "link", onSelect: () => vm.openAdd() },
    ...(s.canAddFiles ? [{ id: "file", label: t("bookmarks.addFile"), icon: "file" as const, onSelect: () => vm.openPicker("file") }] : []),
    { id: "page", label: t("bookmarks.addPage"), icon: "page", onSelect: () => vm.openPicker("page") },
    { id: "sep", separator: true },
    { id: "folder", label: t("bookmarks.addFolder"), icon: "folder", onSelect: () => vm.openAddFolder() },
  ];
  const addButton = s.canEdit ? (
    <Menu items={addMenu} testID={`${testID}-add-menu`}>
      {s.tree.length === 0 ? (
        <Button variant="ghost" size="sm" icon="plus" label={t("bookmarks.add")} onPress={() => undefined} testID={`${testID}-add`} />
      ) : (
        <IconButton icon="plus" size="sm" accessibilityLabel={t("bookmarks.add")} onPress={() => undefined} testID={`${testID}-add`} />
      )}
    </Menu>
  ) : null;

  // Folders are one level deep (Slack): a chip dragged onto a folder's middle moves into it; folders don't nest.
  const isFolder = (id: string) => s.tree.some((n) => n.bookmark.id === id && Bookmarks.bookmarkKind(n.bookmark) === "folder");

  const isFolderEditor = e !== undefined && e.kind === "folder";
  const editorTitle = e === undefined ? "" : e.id === undefined ? t(e.kind === "folder" ? "bookmarks.addFolder" : "bookmarks.add") : t(e.kind === "folder" ? "bookmarks.editFolder" : "bookmarks.edit");

  return (
    <>
      <Stack paddingX="5" paddingY="1">
        <SortableList
          items={s.tree.map((n) => ({ id: n.bookmark.id, node: n }))}
          wrap
          disabled={!s.canEdit}
          onMove={(id, index) => vm.move(id, undefined, index)}
          canDropInto={(id, target) => isFolder(target) && !isFolder(id)}
          onDropInto={(id, folderId) => vm.moveToFolder(id, folderId)}
          accessibilityLabel={t("bookmarks.bar")}
          testID={testID}
          trailing={addButton}
          renderItem={({ node }) => (Bookmarks.bookmarkKind(node.bookmark) === "folder" ? folderPopover(node) : chip(node.bookmark, () => open(node.bookmark)))}
        />
      </Stack>
      <Sheet open={e !== undefined} onClose={() => vm.closeEditor()} title={editorTitle} testID={`${testID}-sheet`}>
        {e === undefined ? null : (
          <Stack gap="3" padding="4">
            {isFolderEditor ? null : (
              <TextField label={t("bookmarks.link")} value={e.url} onChange={(v) => vm.setUrl(v)} placeholder="https://" autoFocus error={e.problem === undefined ? undefined : tr(e.problem)} testID={`${testID}-url`} />
            )}
            <TextField
              label={t(isFolderEditor ? "bookmarks.folderName" : "bookmarks.name")}
              value={e.title}
              onChange={(v) => vm.setTitle(v)}
              placeholder={isFolderEditor ? undefined : t("bookmarks.namePlaceholder")}
              autoFocus={isFolderEditor}
              error={isFolderEditor && e.problem !== undefined ? tr(e.problem) : undefined}
              testID={`${testID}-title`}
            />
            <Stack direction="horizontal" gap="2" justify="end">
              <Button variant="ghost" label={t("common.cancel")} onPress={() => vm.closeEditor()} testID={`${testID}-cancel`} />
              <Button variant="primary" label={t("bookmarks.save")} loading={e.saving} disabled={isFolderEditor ? e.title.trim() === "" : e.url.trim() === ""} onPress={() => void vm.save()} testID={`${testID}-save`} />
            </Stack>
          </Stack>
        )}
      </Sheet>
      <Sheet open={p !== undefined} onClose={() => vm.closePicker()} title={t(p?.kind === "file" ? "bookmarks.addFile" : "bookmarks.addPage")} testID={`${testID}-picker`}>
        {p === undefined ? null : (
          <Stack gap="2" padding="4">
            <TextField label={t(p.kind === "file" ? "bookmarks.searchFiles" : "bookmarks.searchPages")} value={p.query} onChange={(v) => vm.setPickerQuery(v)} autoFocus testID={`${testID}-picker-query`} />
            {p.loading ? (
              <Spinner />
            ) : p.failed ? (
              <Text variant="footnote" color="text.secondary">
                {t("bookmarks.pickFailed")}
              </Text>
            ) : p.items.length === 0 ? (
              <Text variant="footnote" color="text.secondary">
                {t(p.kind === "file" ? "bookmarks.noFiles" : "bookmarks.noPages")}
              </Text>
            ) : (
              <Stack>
                {p.items.map((item) => (
                  <ListRow
                    key={item.id}
                    title={item.title}
                    density="compact"
                    leading={
                      item.icon !== undefined && !item.icon.startsWith("icon:") ? (
                        <Text variant="body">{item.icon}</Text>
                      ) : (
                        <Icon name={p.kind === "page" ? "page" : (Files.fileIcon(Files.fileKind(item.mime ?? "", item.title)) as IconName)} size="icon.sm" color="text.secondary" />
                      )
                    }
                    onPress={() => void vm.pick(item.id)}
                    testID={`${testID}-picker-${item.id}`}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </Sheet>
    </>
  );
}
